import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { IGif } from '@giphy/js-types';
import { Gif } from '@giphy/react-components';
import { Close } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { darken } from '@mui/system';
import { CommentMediaTypeEnum } from '@juno/client-api/model';

interface MediaPreviewProps {
  type: CommentMediaTypeEnum;
  url?: string;
  onDelete?: () => void;
  gifId?: string;
  portraitWidth?: number;
  landscapeWidth?: number;
}

const PORTRAIT_DEFAULT_WIDTH = 120;
const LANDSCAPE_DEFAULT_WIDTH = 180;

const MediaPreview: React.FC<MediaPreviewProps> = ({
  url, // cloudinary url for image
  gifId, // id of the gif
  onDelete,
  portraitWidth = PORTRAIT_DEFAULT_WIDTH,
  landscapeWidth = LANDSCAPE_DEFAULT_WIDTH,
}) => {
  const [gif, setGif] = useState<IGif | null>(null);
  const gf = new GiphyFetch(process.env.NX_GIPHY_API_KEY || '');

  const fetchGif = async (gifId: string) => {
    try {
      const { data } = await gf.gif(gifId);
      setGif(data);
    } catch (error) {
      console.error('Error fetching GIF:', error);
    }
  };

  useEffect(() => {
    if (gifId) {
      fetchGif(gifId);
    }
  }, [gifId]);

  return (
    <Box
      position={'relative'}
      width={
        !gif
          ? 200
          : gif.images.original.height > gif.images.original.width
          ? portraitWidth
          : landscapeWidth
      }
    >
      {onDelete && (
        <IconButton
          size='small'
          onClick={() => onDelete()}
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            zIndex: 2,
            background: '#dbdfe8',
            height: 20,
            width: 20,
            '&:hover': { background: darken('#dbdfe8', 0.08) },
          }}
          aria-label='delete'
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {gif && (
        <Gif
          gif={gif}
          width={
            gif.images.original.height > gif.images.original.width ? portraitWidth : landscapeWidth
          }
          noLink
          hideAttribution
          style={{ cursor: 'default', borderRadius: 12 }}
        />
      )}
      {url && <PreviewImage src={url} alt='' />}
    </Box>
  );
};

export default MediaPreview;

export const PreviewImage = styled('img')(() => ({
  margin: '0',
  display: 'block',
  maxWidth: '200px',
  maxHeight: '100%',
  borderRadius: 12,
}));
